/**
 * Rewind — Phase 5.A
 *
 * Drag a timeline cursor across the past 7 days; the panels below
 * render the pipeline state (mac-status + vps-status) as it was at
 * the selected moment.
 *
 * Snapshots are written by console-reporter on 10-min boundaries to
 * R2:_console/snapshots/<source>/<ISO>.json. The worker exposes
 * /api/snapshots (list timestamps that have BOTH mac+vps) and
 * /api/snapshot?at=<ISO>&source=mac-status (resolves to the closest
 * available snapshot).
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import { api } from "../data/api";
import type {
  SnapshotsIndex,
  MacStatus, VpsStatus, LaunchAgentStatus,
} from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface ViewState {
  /** Cursor position — index into snapshotsIndex.timestamps (latest by default) */
  cursorIdx:    number;
  /** Currently loaded snapshot payloads */
  mac:          MacStatus | null;
  vps:          VpsStatus | null;
  loading:      boolean;
  error:        Error | null;
  loadedFor:    string | null;
}
const view: ViewState = {
  cursorIdx:    -1,            // -1 means "use latest"
  mac:          null,
  vps:          null,
  loading:      false,
  error:        null,
  loadedFor:    null,
};

export function renderRewind(onChange: () => void): TemplateResult {
  const e = store.get<SnapshotsIndex>("snapshotsIndex");
  if (e.error)              return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data)              return emptyView();

  const idx = e.data;
  if (idx.timestamps.length === 0) return notReadyView();

  // Default cursor to the most recent snapshot
  if (view.cursorIdx < 0) view.cursorIdx = idx.timestamps.length - 1;
  if (view.cursorIdx > idx.timestamps.length - 1) {
    view.cursorIdx = idx.timestamps.length - 1;
  }

  const at = idx.timestamps[view.cursorIdx]!;
  if (view.loadedFor !== at) {
    void loadSnapshots(at, onChange);
  }

  return html`
    <div class="rewind">
      ${headerCard(idx, at)}
      ${scrubber(idx, onChange)}
      ${stateView(at)}
    </div>
  `;
}

// =============================================================
// Header — meta + ✨
// =============================================================
function headerCard(idx: SnapshotsIndex, at: string): TemplateResult {
  const ageMin = Math.round((Date.now() - Date.parse(at)) / 60_000);
  return html`
    <section class="panel rewind-header">
      <div class="rewind-header__row">
        <div class="rewind-header__metric">
          <span class="rewind-header__label">cursor</span>
          <span class="rewind-header__value">${humanTime(at)}</span>
          <span class="rewind-header__sub">${humanAgo(ageMin)}</span>
        </div>
        <div class="rewind-header__metric">
          <span class="rewind-header__label">snapshots</span>
          <span class="rewind-header__value">${idx.timestamps.length}</span>
          <span class="rewind-header__sub">${idx.interval_minutes}-min cadence</span>
        </div>
        <div class="rewind-header__metric">
          <span class="rewind-header__label">range</span>
          <span class="rewind-header__value-sm">
            ${humanTime(idx.earliest ?? "")}
          </span>
          <span class="rewind-header__sub">to ${humanTime(idx.latest ?? "")}</span>
        </div>
        <span class="rewind-header__spacer"></span>
        ${renderExplainButton({
          label: "Explain this state",
          context: () => buildExplainContext(at, view.mac, view.vps),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Timeline scrubber
// =============================================================
function scrubber(idx: SnapshotsIndex, onChange: () => void): TemplateResult {
  const max = Math.max(0, idx.timestamps.length - 1);

  // Tick labels — hour boundaries within the window
  const ticks: { label: string; pct: number }[] = [];
  if (idx.timestamps.length > 1) {
    const start = Date.parse(idx.timestamps[0]!);
    const end   = Date.parse(idx.timestamps[idx.timestamps.length - 1]!);
    const span  = end - start;
    const stepMs = pickTickStep(span);
    const firstTick = Math.ceil(start / stepMs) * stepMs;
    for (let t = firstTick; t <= end; t += stepMs) {
      ticks.push({
        label: humanTimeShort(new Date(t).toISOString()),
        pct:   ((t - start) / span) * 100,
      });
    }
  }

  // Move the cursor to a specific index — used by every step button.
  // We imperatively set the slider DOM value because the input no
  // longer carries a lit-html property binding (the binding fights
  // with native drag, snapping the thumb back mid-gesture).
  const move = (newIdx: number) => {
    view.cursorIdx = newIdx;
    const inp = document.querySelector(".rewind-scrub__range") as HTMLInputElement | null;
    if (inp) inp.value = String(newIdx);
    onChange();
  };

  return html`
    <section class="panel rewind-scrub">
      <div class="rewind-scrub__bar">
        ${ticks.map((t) => html`
          <span class="rewind-scrub__tick" style="left:${t.pct}%;">
            <span class="rewind-scrub__tick-label">${t.label}</span>
          </span>
        `)}
        <input
          type="range"
          class="rewind-scrub__range"
          min="0"
          max=${max}
          step="1"
          value=${String(view.cursorIdx)}
          @change=${(e: Event) => {
            view.cursorIdx = Number((e.target as HTMLInputElement).value);
            onChange();
          }}
        />
      </div>
      <div class="rewind-scrub__controls">
        <button class="rewind-btn"
                @click=${() => move(0)}
                ?disabled=${view.cursorIdx === 0}>⇤ earliest</button>
        <button class="rewind-btn"
                @click=${() => move(Math.max(0, view.cursorIdx - 1))}
                ?disabled=${view.cursorIdx === 0}>← step back</button>
        <span class="rewind-scrub__pos">
          ${view.cursorIdx + 1} / ${idx.timestamps.length}
        </span>
        <button class="rewind-btn"
                @click=${() => move(Math.min(max, view.cursorIdx + 1))}
                ?disabled=${view.cursorIdx === max}>step fwd →</button>
        <button class="rewind-btn rewind-btn--primary"
                @click=${() => move(max)}
                ?disabled=${view.cursorIdx === max}>now ⇥</button>
      </div>
    </section>
  `;
}

// =============================================================
// State-at-time view
// =============================================================
function stateView(at: string): TemplateResult {
  if (view.loading && !view.mac) return loadingState(at);
  if (view.error)                return loadErrorState(view.error);
  if (!view.mac && !view.vps)    return loadingState(at);

  return html`
    <div class="rewind-grid">
      ${vpsCard(view.vps)}
      ${macCard(view.mac)}
      ${agentsCard(view.mac)}
    </div>
  `;
}

function vpsCard(v: VpsStatus | null): TemplateResult {
  if (!v) return html`<section class="panel rewind-card"><h3>VPS</h3><div class="rewind-card__empty">no data</div></section>`;
  const lastScrapeMs = v.last_scrape_at ? Date.parse(v.last_scrape_at) : 0;
  const ageMin = lastScrapeMs ? Math.round((Date.now() - lastScrapeMs) / 60_000) : -1;
  return html`
    <section class="panel rewind-card">
      <h3>VPS scraper</h3>
      <div class="rewind-card__metrics">
        <div class="rewind-kv"><span>cadence</span><strong>${v.cadence}</strong></div>
        <div class="rewind-kv"><span>last scrape</span><strong>
          ${ageMin >= 0 ? `${ageMin}m ago` : "never"}
        </strong></div>
        <div class="rewind-kv"><span>disk free</span><strong>${v.disk_free_gb} GB</strong></div>
        <div class="rewind-kv"><span>mem free</span><strong>${v.memory_free_gb} GB</strong></div>
        ${v.skip_reason ? html`
          <div class="rewind-kv"><span>skip reason</span>
            <strong style="color:var(--accent-amber);">${v.skip_reason}</strong></div>
        ` : ""}
      </div>
      ${(v.recent_errors ?? []).length > 0 ? html`
        <h4>Recent errors</h4>
        <ul class="rewind-errs">
          ${v.recent_errors.slice(0, 3).map((e) => html`
            <li><code>${humanTimeShort(e.ts)}</code> ${e.message}</li>
          `)}
        </ul>
      ` : ""}
    </section>
  `;
}

function macCard(m: MacStatus | null): TemplateResult {
  if (!m) return html`<section class="panel rewind-card"><h3>Mac</h3><div class="rewind-card__empty">no data</div></section>`;
  return html`
    <section class="panel rewind-card">
      <h3>Mac host</h3>
      <div class="rewind-card__metrics">
        <div class="rewind-kv"><span>hostname</span><strong>${m.hostname}</strong></div>
        <div class="rewind-kv"><span>disk free</span><strong>${m.disk_free_gb.toFixed(1)} GB</strong></div>
        <div class="rewind-kv"><span>mem free</span><strong>${m.memory_free_gb.toFixed(1)} GB</strong></div>
        <div class="rewind-kv"><span>load avg</span>
          <strong>${m.load_avg.map((n) => n.toFixed(2)).join(" / ")}</strong></div>
      </div>
    </section>
  `;
}

function agentsCard(m: MacStatus | null): TemplateResult {
  const agents = (m?.launchagents ?? []).filter((a) => a.loaded !== false);
  const failed = agents.filter((a) => a.last_exit_code != null && a.last_exit_code !== 0);
  return html`
    <section class="panel rewind-card rewind-card--wide">
      <h3>LaunchAgents · ${agents.length} loaded${failed.length > 0 ? ` · ${failed.length} failed` : ""}</h3>
      <div class="rewind-agents">
        ${agents.map((a) => agentDot(a))}
      </div>
      ${failed.length > 0 ? html`
        <h4>Failures at this snapshot</h4>
        <ul class="rewind-errs">
          ${failed.map((a) => html`
            <li>
              <code>${a.label.replace("com.yurysankov.", "")}</code>
              exit ${a.last_exit_code} · last fire ${
                a.last_fire ? humanTimeShort(a.last_fire) : "never"
              }
            </li>
          `)}
        </ul>
      ` : ""}
    </section>
  `;
}

function agentDot(a: LaunchAgentStatus): TemplateResult {
  const cls = a.last_exit_code != null && a.last_exit_code !== 0 ? "is-bad"
            : a.last_fire ? "is-ok"
            : "is-mute";
  return html`
    <span class="status-dot ${cls}"
          title="${a.label.replace("com.yurysankov.", "")} · last fire ${
            a.last_fire ? humanTimeShort(a.last_fire) : "never"
          }${a.last_exit_code != null ? ` · exit ${a.last_exit_code}` : ""}"
          style="margin:auto;cursor:help;"></span>
  `;
}

// =============================================================
// Snapshot loading
// =============================================================
async function loadSnapshots(at: string, onChange: () => void): Promise<void> {
  if (view.loading) return;
  view.loading = true;
  view.error = null;
  view.loadedFor = at;
  try {
    const [mac, vps] = await Promise.all([
      api.snapshotAt(at, "mac-status"),
      api.snapshotAt(at, "vps-status"),
    ]);
    view.mac = (mac.payload as MacStatus | null) ?? null;
    view.vps = (vps.payload as VpsStatus | null) ?? null;
    view.loading = false;
    onChange();
  } catch (e) {
    view.loading = false;
    view.error = e instanceof Error ? e : new Error(String(e));
    onChange();
  }
}

// =============================================================
// Helpers
// =============================================================
function humanTime(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
function humanTimeShort(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit", minute: "2-digit",
  });
}
function humanAgo(min: number): string {
  if (min === 0)            return "just now";
  if (min < 60)             return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24)               return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function pickTickStep(spanMs: number): number {
  // Pick a reasonable tick interval given span: 1h / 6h / 1d
  if (spanMs <= 6 * 3600_000)   return 3600_000;       // 1h
  if (spanMs <= 24 * 3600_000)  return 6 * 3600_000;   // 6h
  return 24 * 3600_000;                                 // 1d
}

function buildExplainContext(at: string, mac: MacStatus | null, vps: VpsStatus | null): string {
  const lines = [
    `Pipeline state at ${at}:`,
    "",
  ];
  if (vps) {
    lines.push(
      `VPS: cadence=${vps.cadence}, last_scrape=${vps.last_scrape_at ?? "—"}, ` +
      `disk=${vps.disk_free_gb}GB, mem=${vps.memory_free_gb}GB, ` +
      `errors=${(vps.recent_errors ?? []).length}`,
    );
  }
  if (mac) {
    const agents = (mac.launchagents ?? []).filter((a) => a.loaded !== false);
    const failed = agents.filter((a) => a.last_exit_code != null && a.last_exit_code !== 0);
    lines.push(
      `Mac: hostname=${mac.hostname}, disk=${mac.disk_free_gb.toFixed(1)}GB, ` +
      `mem=${mac.memory_free_gb.toFixed(1)}GB, load=${mac.load_avg.join("/")}`,
    );
    lines.push(`LaunchAgents: ${agents.length} loaded, ${failed.length} failed.`);
    if (failed.length > 0) {
      lines.push(`Failed: ${failed.map((a) => `${a.label}(exit ${a.last_exit_code})`).join(", ")}`);
    }
  }
  return lines.join("\n");
}

// =============================================================
// State views
// =============================================================
function notReadyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:520px;margin:0 auto;">
        <p style="font-size:15px;color:var(--text-primary);margin:0 0 8px;">
          No snapshots yet
        </p>
        <p style="margin:0;">
          The Rewind scrubber needs at least 2 snapshots in
          <code style="color:var(--accent-blue-light);">R2:_console/snapshots/</code>.
          The console-reporter writes one every 10 minutes — first
          natural snapshot lands at the next :00, :10, :20… UTC minute.
        </p>
      </div>
    </div>
  `;
}

function loadingState(at: string): TemplateResult {
  return html`
    <section class="panel" style="padding:32px;text-align:center;color:var(--text-tertiary);">
      Loading state at ${humanTime(at)}…
    </section>
  `;
}

function loadErrorState(err: Error): TemplateResult {
  return html`
    <section class="panel" style="padding:24px;border-color:rgba(255,69,58,0.32);">
      <div style="color:var(--accent-red-light);font-size:12px;line-height:1.5;
                   font-family:monospace;white-space:pre-wrap;">
        ${err.message}
      </div>
    </section>
  `;
}

function skeletonView(): TemplateResult {
  return html`
    <div class="rewind">
      <section class="panel" style="height:80px;"></section>
      <section class="panel" style="height:60px;"></section>
      <section class="panel" style="min-height:300px;"></section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;">
        No snapshot index. Reporter may not be running.
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Rewind</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
